import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';
import { getPageAppending } from '../../../../../common/util/Functions';
import { useStorebandInfo } from '../../../../../common/util/Hook';
import Grid from '../../../../../common/Grid/Grid';
import AppMessage from '../../../../../common/AppMessage/AppMessage';
import { HeaderLink, Spinner } from '../../../../../common/share/ShareComponents';
import { RouterConfig } from '../../../../../app/Router';
import { State } from '../../../../../domain/State';
import CantChangeProfileModal from '../CantChangeProfileModal';
import SavingProfileGraph from './graph/SavingProfileGraph';
import SavingProfileFundPerformance from './SavingProfileFundPerformance';
import safeArray from '../../../util/SafeArray';
import { SavingProfileInfo } from './SavingProfileInfo';
import { URL_KEY_CONFIRM_SWITCH, URL_KEY_SWITCH } from '../switch/SwitchSavingProfilePage';
import '../../../Arbeidsgiver.scss';
import '../../Storebrand.scss';
import { useTranslate } from "../../../../../hooks/useTranslate";
import { PensionProduct } from "../../types";

type Props = RouteComponentProps<{ id?: string; isin?: string }> &
    RouterConfig & {
        owned?: boolean;
    };

const SavingProfileDetailsPage = (props: Props): JSX.Element => {
    const { owned } = props;
    const selectable = !owned;
    const storebrandState = useSelector((state: State) => state?.Storebrand);
    const appState = useSelector((state: State) => state?.App);
    const pensionState = useSelector((state: State) => state?.Pensjon);

    const productDescription = useMemo(
        () =>
            pensionState?.arbeidsgiversPensjonsSparing?.find(
                (product: PensionProduct) => product.agreementNumber === props.match.params.id
            ).productDescription,
        [pensionState, props.match.params.id]
    );

    const agreementInfo = useMemo(
        () => storebrandState?.info?.[props.match?.params.id || ''],
        [storebrandState, props.match?.params.id]
    );

    const ownedFund = useMemo(
        () => safeArray(agreementInfo?.funds).find((f) => f.isin === props.match?.params.isin),
        [agreementInfo, props.match?.params.isin]
    );

    const ownsMultipleFunds = useMemo(() => agreementInfo?.funds.length > 1, [agreementInfo])

    const selectableProfile = useMemo(
        () => safeArray(agreementInfo?.selectableProfiles).find((f) => f.isin === props.match?.params.isin),
        [agreementInfo, props.match?.params.isin]
    );

    const profileSwitchNotAllowedReasons = useMemo(() => {
        return safeArray(agreementInfo?.profileSwitchStates)
            .filter((state) => !state.allowed)
            .map((state) => state.reason);
    }, [agreementInfo]);

    const profileSwitchIsAllowed = useMemo(
        () => !profileSwitchNotAllowedReasons.length,
        [profileSwitchNotAllowedReasons]
    );

    const [cantSwitchModalOpen, setCantSwitchModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const appending = useMemo(() => getPageAppending(props), []);

    const translate = useTranslate();
    useStorebandInfo({ Storebrand: storebrandState }, props.action);

    useEffect(() => {
        if (agreementInfo) setLoading(false);
    }, [agreementInfo]);

    const changeProfile = () => {
        if (profileSwitchIsAllowed) {
            // href with replaced current location
            window.location.href = `${location.href.split('/').slice(0, -1).join('/')}/switch`;
        } else {
            setCantSwitchModalOpen(true);
        }
    };

    return (
        <>
            <Grid
                namespace="sparing-top"
                list={[
                    {
                        layout: 'twelve',
                        element: <HeaderLink text={productDescription} length={ownsMultipleFunds ? 1 : 2} />,
                    },
                ]}
            />

            <Grid
                namespace="sparing-header"
                list={[
                    {
                        layout: 'twelve',
                        element: <h1>{(ownedFund || selectableProfile)?.name}</h1>,
                    },
                ]}
            />

            <div className={`arbeidsgiver-profil-common-wrapper ${appending || 'none-appending'}`}>
                <Grid
                    list={[
                        {
                            layout: 'six',
                            element: (
                                <>
                                    <AppMessage
                                        appMessage={appState?.serviceMessage}
                                        context="STOREBRAND_RISK_PROFILE"
                                    />

                                    {!loading && (
                                        <>
                                            {(ownedFund || selectableProfile)?.description ? (
                                                <article className="paragraph">
                                                    {(ownedFund || selectableProfile)?.description}
                                                </article>
                                            ) : (
                                                <div className="divider" />
                                            )}

                                            {owned && !ownsMultipleFunds && (
                                                <div className="change-profile-button-wrapper paragraph">
                                                    <StandardButton
                                                        icon={IconTypes.PENN_24}
                                                        size="small"
                                                        buttonType="negative"
                                                        onClick={changeProfile}
                                                    >
                                                        {translate('changeProfile')}
                                                    </StandardButton>
                                                </div>
                                            )}

                                            <div className="only-for-mobile">
                                                {(ownedFund || selectableProfile) && (
                                                    <SavingProfileInfo
                                                        owned={!!owned}
                                                        riskReductionEnabled={
                                                            agreementInfo?.riskReduction10YearsBeforePension
                                                        }
                                                        isReductionTarget={
                                                            agreementInfo?.riskReductionProfile?.isin ===
                                                            props.match?.params.isin
                                                        }
                                                        customerFund={ownedFund}
                                                        selectableProfile={selectableProfile}
                                                    />
                                                )}
                                            </div>

                                            {props.match?.params.isin && (
                                                <SavingProfileGraph isin={props.match?.params.isin} />
                                            )}
                                            {(ownedFund || selectableProfile)?.fundPerformance && (
                                                <SavingProfileFundPerformance
                                                    fundPerformance={(ownedFund || selectableProfile)?.fundPerformance}
                                                />
                                            )}
                                        </>
                                    )}
                                    {selectable && agreementInfo && (
                                        <div className="change-profile-button-wrapper">
                                            <Link
                                                linkType="button-positive"
                                                href={location.href.replace(URL_KEY_SWITCH, URL_KEY_CONFIRM_SWITCH)}
                                            >
                                                {translate('selectSavingProfile')}
                                            </Link>
                                        </div>
                                    )}

                                    {loading && <Spinner type="linkPanel" />}
                                </>
                            ),
                        },
                        {
                            layout: 'six',
                            element: (
                                <>
                                    <div className="only-for-desktop">
                                        <h3>Detajler</h3>
                                        {(ownedFund || selectableProfile) && (
                                            <SavingProfileInfo
                                                owned={!!owned}
                                                riskReductionEnabled={agreementInfo?.riskReduction10YearsBeforePension}
                                                isReductionTarget={
                                                    agreementInfo?.riskReductionProfile?.isin ===
                                                    props.match?.params.isin
                                                }
                                                customerFund={ownedFund}
                                                selectableProfile={selectableProfile}
                                            />
                                        )}
                                    </div>
                                </>
                            ),
                        },
                    ]}
                />
            </div>

            <CantChangeProfileModal
                onClose={() => setCantSwitchModalOpen(false)}
                open={cantSwitchModalOpen}
                reasons={profileSwitchNotAllowedReasons}
            />
        </>
    );
};

export default SavingProfileDetailsPage;
